/**
 * AppLayout - Shared layout for all app pages with bottom navigation
 *
 * Provides consistent styling and navigation across Vault, Bot, Deposit, Settings
 */

import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import WalletSecurityBanner from '../Wallet/WalletSecurityBanner';
import apiService from '../../services/api';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  showHeader?: boolean;
  headerRight?: React.ReactNode;
}

// Bottom Navigation Bar
const BottomNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    {
      path: '/vault',
      label: 'Vault',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
        </svg>
      ),
    },
    {
      path: '/bot',
      label: 'Bot',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      path: '/wallet',
      label: 'Wallet',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      path: '/swap',
      label: 'Swap',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      ),
    },
    {
      path: '/referral',
      label: 'Referrals',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
    },
    {
      path: '/settings',
      label: 'Settings',
      icon: (
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-800 z-40 pb-safe">
      <div className="flex justify-around py-2">
        {navItems.map(item => {
          const isActive = location.pathname === item.path;
          return (
            <motion.button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center p-2 min-w-[64px] relative ${
                isActive ? 'text-white' : 'text-gray-500'
              }`}
              whileTap={{ scale: 0.95 }}
            >
              {item.icon}
              <span className="text-xs mt-1">{item.label}</span>
              {isActive && (
                <motion.div
                  className="absolute bottom-1 w-1 h-1 bg-white rounded-full"
                  layoutId="navIndicator"
                />
              )}
            </motion.button>
          );
        })}
      </div>
    </nav>
  );
};

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  title,
  showHeader = true,
  headerRight,
}) => {
  const { user } = useAuth();
  const [walletSecurity, setWalletSecurity] = useState<{
    has_exported_key: boolean;
    should_show_reminder: boolean;
  } | undefined>(user?.wallet_security);

  // Fetch wallet security status on mount if not available
  useEffect(() => {
    if (!walletSecurity && user) {
      apiService.profile.get().then((profile: any) => {
        if (profile.wallet_security) {
          setWalletSecurity(profile.wallet_security);
        }
      }).catch(() => {
        // Ignore errors - banner just will not show
      });
    }
  }, [user, walletSecurity]);

  const handleReminderDismiss = () => {
    setWalletSecurity(prev => prev ? { ...prev, should_show_reminder: false } : undefined);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Main content with bottom padding for nav */}
      <div className="pb-20">
        {/* Header */}
        {showHeader && title && (
          <header className="sticky top-0 bg-black/90 backdrop-blur-sm z-30 px-4 py-4 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <h1 className="text-xl font-semibold">{title}</h1>
              {headerRight}
            </div>
          </header>
        )}

        {/* Page content */}
        <main className="px-4 py-6">
          {/* Wallet Security Reminder Banner */}
          <WalletSecurityBanner 
            walletSecurity={walletSecurity}
            onDismiss={handleReminderDismiss}
          />
          {children}
        </main>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation />
    </div>
  );
};

export default AppLayout;
